import { useEffect } from 'react';
import { useRouter } from 'next/router';
import { 
  getSelectedCountryByUser, 
  setSelectedCountryByUser,
  clearSelectedCountryByUser,
  getCookie
} from '@/utils/cookieutils';
import { 
  KNOWN_COUNTRIES, 
  ensureLocalesLoaded, 
  detectCountryFromPath 
} from '@/Config/countryConstants';

/**
 * Hook to sync cookies based on URL changes
 * Updates SelectedCountryByUser and stayGlobal cookies when users manually navigate
 */
export const useUrlBasedCookieSync = () => {
  const router = useRouter();

  useEffect(() => {
    const handleRouteChange = async () => {
      // Ensure locales are loaded before processing
      await ensureLocalesLoaded();
      
      const currentPath = router.asPath.split('?')[0].split('#')[0];
      const currentCountry = detectCountryFromPath(currentPath);
      const currentCookie = getSelectedCountryByUser();
      const stayGlobal = getCookie('stayGlobal');

      // Case 1: User navigated to root path (/)
      // Only clear country cookie if stayGlobal is explicitly set (South Africa selection)
      if (currentPath === '/') {
        if (stayGlobal === 'true') {
          // User explicitly selected South Africa, clear the country cookie
          if (currentCookie) {
            setTimeout(() => {
              clearSelectedCountryByUser();
            }, 0);
          }
        }
        // If stayGlobal is not set, keep the existing country cookie
        return;
      }

      // Case 2: User navigated to a country-specific path
      if (currentCountry && KNOWN_COUNTRIES.includes(currentCountry)) {
        // If the current cookie doesn't match the URL country, update it
        if (currentCookie !== currentCountry) {
          setSelectedCountryByUser(currentCountry);
        }
        return;
      }

      // Case 3: User navigated to a global page (no country in URL)
      // If user has stayGlobal=true, respect that choice and don't override
      if (!currentCountry && stayGlobal === 'true') {
        return;
      }
      
      // If user has a country cookie but is on a global page, keep the cookie
      // This allows global pages to show country-specific data based on the cookie
      if (!currentCountry && currentCookie) {
        return;
      }

      // Case 4: User navigated to a global page without any country cookie
      // This is the default state, no action needed
      if (!currentCountry && !currentCookie) {
        return;
      }
    };

    // Handle initial load
    if (router.isReady) {
      handleRouteChange();
    }

    // Listen for route changes
    router.events.on('routeChangeComplete', handleRouteChange);

    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [router.asPath, router.isReady, router.events]);
};
